import { useSyncExternalStore } from "react";

export interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PwaState {
  install: InstallPrompt | null;
  ios: boolean;
  installed: boolean;
  /** This window is the installed app, not a browser tab (installing from a tab leaves it false). */
  standalone: boolean;
  waiting: ServiceWorker | null;
  dismissed: boolean;
}

const initial: PwaState = {
  install: null,
  ios: false,
  installed: false,
  standalone: false,
  waiting: null,
  dismissed: false,
};
let state = initial;
const listeners = new Set<() => void>();
function setState(patch: Partial<PwaState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export function usePwaState() {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => initial,
  );
}

export function dismissPwaUpdate() {
  setState({ dismissed: true });
}

export async function promptPwaInstall() {
  const prompt = state.install;
  if (!prompt) return;
  setState({ install: null }); // Each browser event can be prompted only once.
  await prompt.prompt();
  if ((await prompt.userChoice).outcome === "accepted") {
    setState({ installed: true, ios: false });
  }
}

/** No reload happens until this explicit user action. Other tabs keep working. */
export function activatePwaUpdate(reload = () => window.location.reload()) {
  const worker = state.waiting;
  if (!worker) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    let finished = false;
    const cleanup = () => {
      finished = true;
      clearTimeout(timeout);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        controlled,
      );
    };
    const controlled = () => {
      if (finished || navigator.serviceWorker.controller !== worker) return;
      cleanup();
      resolve();
      reload();
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      setState({ dismissed: false });
      reject(new Error("The update did not take control. Please try again."));
    }, 20_000);
    navigator.serviceWorker.addEventListener("controllerchange", controlled);
    // Another tab may already have activated the worker while this toast was open.
    if (navigator.serviceWorker.controller === worker) controlled();
    else {
      try {
        worker.postMessage({ type: "SUPERVITY_ACTIVATE_UPDATE" });
      } catch (error) {
        cleanup();
        setState({ dismissed: false });
        reject(error);
      }
    }
  });
}

/** One root-mounted owner; install events remain available when menus mount later. */
export function startPwa(production: boolean) {
  let disposed = false;
  const cleanup: Array<() => void> = [];
  const owned = (worker: ServiceWorker | null) =>
    worker && new URL(worker.scriptURL).pathname === "/sw.js";
  if (!production) {
    // Never unregister unrelated workers on a shared development origin.
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker
        .getRegistrations()
        .then(async (registrations) => {
          for (const registration of registrations) {
            if (
              owned(
                registration.active ??
                  registration.waiting ??
                  registration.installing,
              )
            ) {
              await registration.unregister();
            }
          }
        })
        .catch(() => {});
    }
    if ("caches" in window) {
      void caches
        .keys()
        .then((keys) =>
          Promise.all(
            keys
              .filter((key) => key.startsWith("supervity-pwa-"))
              .map((key) => caches.delete(key)),
          ),
        )
        .catch(() => {});
    }
    return () => {};
  }
  if (!window.isSecureContext) return () => {};
  // Installed display modes only. `fullscreen` is excluded: a browser tab in
  // full-screen can match it, and this manifest never requests it.
  const displays = ["standalone", "minimal-ui", "window-controls-overlay"].map(
    (mode) => window.matchMedia(`(display-mode: ${mode})`),
  );
  const updateDisplay = () => {
    const standalone =
      displays.some((display) => display.matches) ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const ios =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const safari =
      /Safari/.test(navigator.userAgent) &&
      !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
    setState({
      installed: standalone,
      standalone,
      ios: ios && safari && !standalone,
    });
  };
  updateDisplay();
  for (const display of displays) {
    display.addEventListener("change", updateDisplay);
  }
  const installPrompt = (event: Event) => {
    event.preventDefault();
    setState({ install: event as InstallPrompt });
  };
  const installed = () =>
    setState({ installed: true, install: null, ios: false });
  window.addEventListener("beforeinstallprompt", installPrompt);
  window.addEventListener("appinstalled", installed);
  cleanup.push(() => {
    for (const display of displays) {
      display.removeEventListener("change", updateDisplay);
    }
    window.removeEventListener("beforeinstallprompt", installPrompt);
    window.removeEventListener("appinstalled", installed);
  });
  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        if (disposed) return;
        const ready = () => {
          if (
            registration.waiting &&
            registration.active &&
            state.waiting !== registration.waiting
          ) {
            setState({ waiting: registration.waiting, dismissed: false });
          }
        };
        const watchInstalling = () => {
          const worker = registration.installing;
          if (!worker) return;
          worker.addEventListener("statechange", ready);
          cleanup.push(() => worker.removeEventListener("statechange", ready));
        };
        ready();
        watchInstalling();
        registration.addEventListener("updatefound", watchInstalling);
        let lastCheck = 0;
        const check = () => {
          if (
            !navigator.onLine ||
            document.visibilityState === "hidden" ||
            Date.now() - lastCheck < 60_000
          )
            return;
          lastCheck = Date.now();
          void registration.update().catch(() => {}); // Offline/check failures are retried later.
        };
        check();
        const interval = window.setInterval(check, 60 * 60 * 1000);
        window.addEventListener("online", check);
        document.addEventListener("visibilitychange", check);
        cleanup.push(() => {
          clearInterval(interval);
          registration.removeEventListener("updatefound", watchInstalling);
          window.removeEventListener("online", check);
          document.removeEventListener("visibilitychange", check);
        });
      })
      .catch((error: unknown) => {
        console.warn(
          "PWA registration failed; the application remains available online.",
          error,
        );
      });
  }
  return () => {
    disposed = true;
    cleanup.forEach((stop) => stop());
  };
}
